/**
 * Ciorna locală + autosalvarea editorului de notițe.
 *
 * Keystroke → localStorage (debounce 500ms), criptat de `noteDraft.ts`.
 * Server save se face DOAR la unmount / buton Salvează.
 *
 * Sunt două hook-uri fiindcă starea e folosită și de bucăți declarate ÎNAINTE
 * de efectele ciornei (hidratarea, mutația de salvare), iar ordinea efectelor
 * din componentă trebuie să rămână cea dinainte de extragere:
 *   1. `useNoteDraftStatus` — doar stare (indicatorul + flag-ul dirty);
 *   2. `useNoteDraft` — programarea ciornei, flush la unmount, beforeunload.
 */
import {
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';
import type { Editor } from '@tiptap/react';
import { clearDraft, writeDraft } from '@/features/notes/noteDraft';
import { isUnchangedFromSaved } from '@/features/notes/editor/draftDecisions';

export type AutoSaveStatus = 'idle' | 'draft' | 'saving' | 'saved';

export interface NoteDraftStatus {
  autoStatus: AutoSaveStatus;
  setAutoStatus: Dispatch<SetStateAction<AutoSaveStatus>>;
  dirtyRef: RefObject<boolean>;
}

export function useNoteDraftStatus(): NoteDraftStatus {
  const [autoStatus, setAutoStatus] = useState<AutoSaveStatus>('idle');
  const dirtyRef = useRef<boolean>(false);
  return { autoStatus, setAutoStatus, dirtyRef };
}

interface UseNoteDraftArgs {
  setAutoStatus: NoteDraftStatus['setAutoStatus'];
  dirtyRef: NoteDraftStatus['dirtyRef'];
  isEdit: boolean;
  hydrated: boolean;
  editor: Editor | null;
  user: { id: string } | null;
  noteId: string | null;
  /** Titlul curent din input. */
  title: string;
  /** HTML-ul de pe server — referința pentru diff. */
  savedHtml: string;
  /** Titlul de pe server (undefined cât timp detaliul nu e încărcat). */
  savedTitle: string | undefined;
  /** Server save cu versionare (fire-and-forget). */
  save: () => void;
}

export interface NoteDraftControls {
  /** Counter incrementat la fiecare editor update — dep pentru `dirty`. */
  editorTick: number;
  scheduleDraftSave: () => void;
  saveDraftExplicit: () => void;
}

export function useNoteDraft({
  setAutoStatus,
  dirtyRef,
  isEdit,
  hydrated,
  editor,
  user,
  noteId,
  title,
  savedHtml,
  savedTitle,
  save,
}: UseNoteDraftArgs): NoteDraftControls {
  const draftTimerRef = useRef<number | null>(null);
  const flushRef = useRef<() => void>(() => {});
  // Counter incrementat la fiecare editor update — folosit ca dep în
  // useMemo(dirty) ca să re-evaluăm chiar când autoStatus nu se schimbă
  // (ex. multiple resize-uri consecutive la aceeași imagine).
  const [editorTick, setEditorTick] = useState(0);

  // Timer-ul de draft rulează după 500ms dintr-un closure vechi — titlul îl
  // citim printr-un ref ca să nu salvăm valoarea de la momentul programării.
  const titleRef = useRef(title);
  titleRef.current = title;

  const scheduleDraftSave = () => {
    if (!isEdit || !hydrated || !editor || !user || !noteId) return;
    setAutoStatus('draft');
    dirtyRef.current = true;
    if (draftTimerRef.current) window.clearTimeout(draftTimerRef.current);
    draftTimerRef.current = window.setTimeout(() => {
      void writeDraft(user.id, noteId, {
        title: titleRef.current.trim(),
        html: editor.getHTML(),
      });
    }, 500);
  };

  // Salvare draft explicită — user click pe butonul dedicat ca să-și dea
  // liniștea că draft-ul e persistat local. Nu face commit server, nu
  // creează version; draft-ul trăiește în localStorage până user-ul
  // apasă „Salvează" (version) sau închide tab-ul.
  const saveDraftExplicit = () => {
    if (!user || !noteId || !editor) return;
    if (draftTimerRef.current) {
      window.clearTimeout(draftTimerRef.current);
      draftTimerRef.current = null;
    }
    void writeDraft(user.id, noteId, {
      title: title.trim(),
      html: editor.getHTML(),
    });
    setAutoStatus('saved');
    window.setTimeout(() => setAutoStatus('idle'), 1500);
  };

  flushRef.current = () => {
    if (!isEdit || !hydrated) return;
    if (!dirtyRef.current) return;
    if (!title.trim()) return;
    dirtyRef.current = false;
    // `dirtyRef` rămâne true și după ce userul tastează și apoi revine la
    // textul inițial. Comparăm cu ce e pe server: fără diferență reală nu
    // salvăm, altfel fiecare ieșire din notiță ar arhiva o versiune identică.
    const unchanged = isUnchangedFromSaved({
      editorHtml: editor ? editor.getHTML() : null,
      savedHtml,
      title,
      savedTitle,
    });
    if (unchanged) {
      if (user && noteId) clearDraft(user.id, noteId);
      return;
    }
    // Fire-and-forget — server save cu versionare.
    save();
  };

  useEffect(() => {
    if (!editor || !isEdit || !hydrated) return;
    const handler = () => {
      scheduleDraftSave();
      setEditorTick((t) => t + 1);
    };
    editor.on('update', handler);
    return () => {
      editor.off('update', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, isEdit, hydrated, user, noteId]);

  useEffect(() => {
    return () => {
      if (draftTimerRef.current) {
        window.clearTimeout(draftTimerRef.current);
        draftTimerRef.current = null;
      }
      // Flush la unmount — generează versiune nouă pe server.
      flushRef.current();
    };
  }, []);

  // Avertizare nativă înainte de close tab dacă există draft
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (dirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
    // `dirtyRef` e un ref stabil primit ca argument — lista rămâne goală, la
    // fel ca înainte de extragerea în hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { editorTick, scheduleDraftSave, saveDraftExplicit };
}
